import { mapActions, mapGetters } from 'vuex';

import { FeatureTypes } from '@/components/Billing/billing-types';
import AppWorkspaceAvatar from '@/components/WorkspaceSelectionMenu/AppWorkspaceAvatar.vue';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { APP_CONFIRMATION_DIALOG_RESPONSES } from '@/components/dialogs/AppDialogs/app-confirmation-dialog.const.js';
import credentials from '@/credentials';
import router from '@/router';
import { WORKSPACE_SELECTION_ROUTE_NAME, BETA_PLAN_ACCOUNT_ACCESS_DENIED } from '@/router/router.const.js';
import { setActiveAccountId, removeUserFromAccount } from '@/store/modules/account/actions.js';
import {
	accounts,
	useAccount,
	joinedAccounts,
	useAccountLink,
	invitedAccounts,
	isUserAccountOwner,
	account as activeAccount,
} from '@/store/modules/account/getters.js';
import { getEntitlements } from '@/store/modules/billing/actions.js';
import { useEntitlement } from '@/store/modules/billing/getters.js';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import ErrorHandlingService from '@/utils/error-handling-service';
export default {
	computed: {
		...mapGetters(['user']),
		...mapGettersShorthand({
			accounts,
			useAccount,
			activeAccount,
			useAccountLink,
			joinedAccounts,
			invitedAccounts,
			isUserAccountOwner,
			useEntitlement,
		}),
		workspaceMenuHandlers() {
			return {
				'go-to-account': this.goToAccount,
				'create-new': this.createNewWorkspace,
				'leave-workspace': this.leaveWorkspace,
				'accept-invitation': this.acceptInvitation,
				'decline-invitation': this.declineInvitation,
			};
		},
		userId() {
			return this.user?.id;
		},
	},
	methods: {
		...mapActions(['joinWorkspace', 'logout']),
		...mapActionsShorthand({ setActiveAccountId, removeUserFromAccount, getEntitlements }),
		openWorkspaceChangedDialog(payload) {
			AppDialogManagerService.openWorkspaceChangedDialog(payload);
		},
		async changeWorkspace(accountId) {
			this.closeMenu();
			const openWorkspaceChangedDialogParams = { account: this.useAccount(accountId) };
			const joinWorkspaceParams = { userId: this.userId, accountLinkId: this.useAccountLink(accountId).id };
			try {
				await this.joinWorkspace(joinWorkspaceParams);
				if ([WORKSPACE_SELECTION_ROUTE_NAME, BETA_PLAN_ACCOUNT_ACCESS_DENIED].includes(this.$route.name)) {
					this.$router.push({ name: 'HomePage' });
				} else {
					if (await this.shouldRedirectToBetaAccountAccessDenied()) {
						this.$router.push({ name: BETA_PLAN_ACCOUNT_ACCESS_DENIED });
					} else {
						this.openWorkspaceChangedDialog(openWorkspaceChangedDialogParams);
					}
				}
			} catch (err) {
				this.$toast.error(`${ErrorHandlingService.extractErrorMessage(err) || 'Failed to join workspace'}`, { duration: 5000 });
			}
		},
		goToAccount(accountId) {
			this.changeWorkspace(accountId);
		},
		async shouldRedirectToBetaAccountAccessDenied() {
			// block beta users from accessing the app
			if (this.user?.profile?.account?.has_beta_plans) {
				await this.getEntitlements();
				const unblockEntitlement = this.useEntitlement(FeatureTypes.UnblockBetaPlans);
				return !unblockEntitlement.has_access;
			}
			return false;
		},
		async leaveWorkspace(accountId) {
			this.closeMenu();
			if (this.isUserAccountOwner(accountId, this.userId)) {
				AppDialogManagerService.openLeaveWorkspaceDialog({ account: this.useAccount(accountId) });
			} else {
				const workspaceName = this.getWorkspaceName(accountId);
				const isLeavingCurrentActiveWorkspace = this.activeAccount?.id === accountId;
				const dialogProps = {
					title: 'Leave workspace',
					dangerText: `Are you sure you want to leave ${workspaceName}? This action will keep all of your personal data and connections available to the workspace admin, and you will be locked out of the workspace.`,
					verificationDescription: 'Please type the name of the workspace to confirm.',
					noButtonText: 'Cancel',
					yesButtonText: 'Leave workspace',
					onYes: () => this.removeUserFromAccount({ userId: this.userId, accountId }),
					confirmationText: workspaceName,
					titleSuffixComponent: { component: AppWorkspaceAvatar, props: { account: this.useAccount(accountId) }, class: 'mb-6' },
					areYouSureText: `Are you sure you want to leave the workspace <h3 style="color: #f55366">&nbsp;${workspaceName}</h3> ?`,
				};
				const dialogRes = await AppDialogManagerService.openDangerConfirmationDialog(dialogProps);
				await AppDialogManagerService.closeAllDialogs(); // Closes settings dialog - because it will catch the router push and ot won't be in the main router if not
				if (dialogRes === APP_CONFIRMATION_DIALOG_RESPONSES.YES && isLeavingCurrentActiveWorkspace) {
					this.redirectToWorkspaceSelection();
				}
			}
		},
		acceptInvitation(accountId) {
			this.changeWorkspace(accountId);
		},
		redirectToWorkspaceSelection() {
			credentials.clear();
			router.push({ name: WORKSPACE_SELECTION_ROUTE_NAME });
		},
		async declineInvitation(accountId) {
			this.closeMenu();
			const dialogProps = {
				title: 'Decline invitation',
				description: `Are you sure you want to decline the invitation to join ${this.getWorkspaceName(accountId)}?`,
				noText: 'Cancel',
				yesText: 'Decline invitation',
			};
			const res = await AppDialogManagerService.openConfirmationDialog(dialogProps);
			if (res === APP_CONFIRMATION_DIALOG_RESPONSES.YES) {
				this.removeUserFromAccount({ userId: this.userId, accountId });
			}
		},
		createNewWorkspace() {
			AppDialogManagerService.openCreateWorkspaceDialog();
			this.closeMenu();
		},
		getWorkspaceName(accountId) {
			return this.useAccount(accountId)?.name;
		},
		closeMenu() {
			this.showMenu = false;
		},
	},
	data() {
		return { showMenu: false };
	},
};
